import { Client, ClientAgencyConfig } from "models/common"
import {
  ClientApproval,
  ClientProfile,
  Company,
  TeamLead,
} from "models/clientAgencyConf/profile"
import { DocType, Document } from "models/documents"
import { http, httpNoTimeout } from "./http"

import { AgencyTeam } from "routes/Base/Admin/TeamMembers/types"
import { ClientPatchPayload } from "models/clientAgencyConf"
import { Paginated } from "./types"

export const fetchClients = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get("clients", { searchParams }).json()
  return res as Paginated<Client>
}

export const updateClientAgencyProfile = async (
  payload: { agencyId: string; clientId: string } & Partial<ClientPatchPayload>,
) => {
  const { agencyId, clientId, ...json } = payload
  const res = await http
    .patch(`agencies/${agencyId}/clients/${clientId}/profile`, {
      json,
    })
    .json()

  return res as any
}

export const updateClientAgencyApproval = async (
  payload: { agencyId?: string; clientId: string } & Partial<ClientApproval>,
) => {
  const { agencyId, clientId, ...approvalFlow } = payload
  const res = await http
    .put(`agencies/${agencyId}/clients/${clientId}/flow`, {
      json: { ...approvalFlow },
    })
    .json()

  return res as any
}

export const updateClientConfigApproval = async (
  payload: { clientId: string } & Partial<ClientApproval>,
) => {
  const { clientId, ...approvalFlow } = payload
  const res = await http
    .put(`client-agency-configs/${clientId}/flow`, {
      json: { ...approvalFlow },
    })
    .json()

  return res as any
}

export const fetchAgencyClientProfile = async (
  _key: string,
  id: string,
  agencyId?: string,
) => {
  const res = await http
    .get(`agencies/${agencyId}/clients/${id}/profile`)
    .json()
  return res as ClientProfile
}

export const fetchClientConfigProfile = async (
  _key: string,
  id: string,
  _agencyId?: string,
) => {
  const res = await http.get(`client-agency-configs/${id}/profile`).json()
  return res as ClientProfile
}

export const fetchAgencyClientApproval = async (
  _key: string,
  id: string,
  agencyId?: string,
) => {
  const res = await http.get(`agencies/${agencyId}/clients/${id}/flow`).json()
  return res as ClientApproval
}

export const fetchClientConfigApproval = async (
  _key: string,
  id: string,
  _agencyId?: string,
) => {
  const res = await http.get(`client-agency-configs/${id}/flow`).json()
  return res as ClientApproval
}

export const fetchAgencyClientUsers = async (
  _key: string,
  id: string,
  agencyId?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`agencies/${agencyId}/clients/${id}/users`, { searchParams })
    .json()
  return res as AgencyTeam[]
}

export const fetchClientDocuments = async (_key: string, id: string) => {
  const res = await http(
    `clients/${id}/documents?sort=DATE_CREATED,DESC`,
  ).json()
  return res as Array<Document>
}

export const fetchClientConfigDocuments = async (_key: string, id: string) => {
  const res = await http(
    `client-agency-configs/${id}/documents?sort=DATE_CREATED,DESC`,
  ).json()
  return res as Array<Document>
}

type UploadClientDocPld = {
  file?: File
  mediaType?: string
  description?: string
  ownerEntityId: string
  documentType?: DocType
}

export const uploadClientDocument = async (pld: UploadClientDocPld) => {
  if (pld.file === undefined) return
  const { file, ownerEntityId, documentType, description } = pld
  const formData = new FormData()
  formData.append("file", file)
  formData.append("name", file.name)
  if (documentType !== undefined) {
    formData.append("documentType", documentType)
  }
  if (description !== undefined) {
    formData.append("description", description)
  }

  const res = await httpNoTimeout
    .post(`clients/${ownerEntityId}/documents`, { body: formData })
    .json()
  return res
}

export const uploadClientConfigDocument = async (pld: UploadClientDocPld) => {
  if (pld.file === undefined) return
  const { file, ownerEntityId, documentType, description } = pld
  const formData = new FormData()
  formData.append("file", file)
  formData.append("name", file.name)
  if (documentType !== undefined) {
    formData.append("documentType", documentType)
  }
  if (description !== undefined) {
    formData.append("description", description)
  }

  const res = await httpNoTimeout
    .post(`client-agency-configs/${ownerEntityId}/documents`, {
      body: formData,
    })
    .json()
  return res
}

export const deleteClientDocument = async (id: string) => {
  await http.delete(`clients/documents/${id}`)
}

export const deleteClientConfigDocument = async (id: string) => {
  await http.delete(`client-agency-configs/documents/${id}`)
}

type AgencyClientType = {
  agencyId: string
  clientId: string
}

export async function activateAgencyClient(payload: AgencyClientType) {
  return await http.patch(
    `agencies/${payload.agencyId}/clients/${payload.clientId}/activate`,
  )
}

export async function deactivateAgencyClient(payload: AgencyClientType) {
  return await http.patch(
    `agencies/${payload.agencyId}/clients/${payload.clientId}/deactivate`,
  )
}

export const fetchClientAgencyConfigs = async (
  _key: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get("client-agency-configs", { searchParams }).json()
  return res as Paginated<ClientAgencyConfig>
}

export const fetchClient = async (_key: string, id: string) => {
  const res = await http.get(`clients/${id}`).json()
  return res as Client
}

export const updateClient = async (pld: { id: string } & Partial<Client>) => {
  const { id, ...body } = pld
  const res = await http.patch(`clients/${id}`, { json: body }).json()
  return res as Client
}

export const fetchAgencyClientTeamLeads = async (
  _key: string,
  clientId: string,
  agencyId?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`agencies/${agencyId}/clients/${clientId}/team-leads`, {
      searchParams,
    })
    .json()
  return res as TeamLead[]
}

export const fetchClientTeamLeads = async (
  _key: string,
  clientId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`client-agency-configs/${clientId}/team-leads`, {
      searchParams,
    })
    .json()
  return res as TeamLead[]
}

export const addAgencyClientTeamLead = async ({
  agencyId,
  clientId,
  teamLeadId,
}: {
  agencyId?: string
  clientId: string
  teamLeadId: string
}) =>
  await http.post(
    `agencies/${agencyId}/clients/${clientId}/team-leads/${teamLeadId}`,
  )

export const deleteAgencyClientTeamLead = async ({
  clientId,
  teamLeadId,
  agencyId,
}: {
  clientId: string
  teamLeadId: string
  agencyId?: string
}) =>
  await http.delete(
    `agencies/${agencyId}/clients/${clientId}/team-leads/${teamLeadId}`,
  )

export const fetchAgencyClientCompanies = async (
  _key: string,
  agencyId?: string,
) => {
  if (!agencyId) return
  const res = await http.get(`agencies/${agencyId}/companies`).json()

  return res as Paginated<Company>
}

export const fetchClientsByAgency = async (id: string) => {
  const res = await http.get(`agencies/${id}/clients`).json()
  return res as Paginated<Client>
}
